import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'

import {
  BookOpen,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarPlus,
  ChevronRight,
  ClipboardList,
  FileMinus2,
  FileText,
  Globe,
  GraduationCap,
  LineChart,
  ListOrdered,
  LogOut,
  MessageSquare,
  Newspaper,
  PhoneCall,
  Send,
  Stethoscope,
  Target,
  User,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react'
import { useAuthStore } from '@/store/AuthStore'
import { Link, useNavigate } from 'react-router-dom'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { useNavigationStore } from '@/store/NavigationStore'
import { hasAccess } from '@/lib/rbac'
import { ModeToggle } from './mode-toggle'

export function AppSidebar() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const clearStack = useNavigationStore((state) => state.clearStack)
  const { setOpenMobile, isMobile } = useSidebar()

  const userRole = user?.role || 'superAdmin'

  const handleSidebarNavigation = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    // Clear navigation stack when using sidebar links (top-level navigation)
    clearStack()
    // Close mobile sidebar after navigation
    if (isMobile) {
      setOpenMobile(false)
    }
    navigate(path)
  }

  return (
    <Sidebar>
      <SidebarHeader>
        <Link to="/" className="flex items-center space-x-2">
          <img src="/logo.png" alt="ProCounsel icon" height={40} width={40} className="h-10 w-10" />

          <div className="flex flex-col leading-tight">
            <h1 className="text-2xl font-bold text-orange-600">ProCounsel</h1>
            <span className="text-xs text-muted-foreground">
              by <span className="font-semibold text-muted-foreground">CatalystAI</span>
            </span>
          </div>
        </Link>
      </SidebarHeader>
      <SidebarSeparator />

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Admin</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {hasAccess(userRole, 'canAccessAnalytics') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/analytics" onClick={handleSidebarNavigation('/analytics')}>
                      <LineChart className="mr-2 h-4 w-4" />
                      Analytics
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessCounsellors') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/" onClick={handleSidebarNavigation('/')}>
                      <Users className="mr-2 h-4 w-4" />
                      Counsellors
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessProBuddies') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/probuddies" onClick={handleSidebarNavigation('/probuddies')}>
                      <UserCheck className="mr-2 h-4 w-4" />
                      ProBuddies
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {userRole === 'superAdmin' && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/users" onClick={handleSidebarNavigation('/users')}>
                      <User className="mr-2 h-4 w-4" />
                      Users
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {userRole === 'superAdmin' && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/enquiries" onClick={handleSidebarNavigation('/enquiries')}>
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Enquiries
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessReferrers') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/referrers" onClick={handleSidebarNavigation('/referrers')}>
                      <Globe className="mr-2 h-4 w-4" />
                      Referrers
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessLeads') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/leads" onClick={handleSidebarNavigation('/leads')}>
                      <Target className="mr-2 h-4 w-4" />
                      Leads
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {userRole === 'superAdmin' && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/create-admin" onClick={handleSidebarNavigation('/create-admin')}>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Create Admin
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessNeetPredictor') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/neet-predictor" onClick={handleSidebarNavigation('/neet-predictor')}>
                      <Stethoscope className="mr-2 h-4 w-4" />
                      NEET College Lookup
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessMyLeads') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/my-leads" onClick={handleSidebarNavigation('/my-leads')}>
                      <PhoneCall className="mr-2 h-4 w-4" />
                      My Leads
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessAbroadLeads') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/abroad-leads" onClick={handleSidebarNavigation('/abroad-leads')}>
                      <Globe className="mr-2 h-4 w-4" />
                      Study Abroad Leads
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessAdminAppointments') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/admin-appointments" onClick={handleSidebarNavigation('/admin-appointments')}>
                      <CalendarClock className="mr-2 h-4 w-4" />
                      Admin Appointments
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessBlogs') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/blogs" onClick={handleSidebarNavigation('/blogs')}>
                      <Newspaper className="mr-2 h-4 w-4" />
                      Blogs
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessWithdrawals') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/withdrawls" onClick={handleSidebarNavigation('/withdrawls')}>
                      <FileMinus2 className="mr-2 h-4 w-4" />
                      Payouts
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessBroadcast') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/broadcast" onClick={handleSidebarNavigation('/broadcast')}>
                      <Send className="mr-2 h-4 w-4" />
                      Broadcast
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessEvents') && (
                <SidebarMenuItem>
                  <Collapsible defaultOpen className="group/collapsible">
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton className="group">
                        <span className="flex items-center gap-2">
                          <CalendarCheck className="mr-2 w-4 h-4" />
                          Events
                        </span>
                        <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-4">
                      <SidebarMenuSub>
                        <SidebarMenuSubItem className="py-2">
                          <a href="/allevents" onClick={handleSidebarNavigation('/allevents')}>
                            <span className="flex">
                              <Calendar className="mr-2 h-4 w-4" />
                              Overview
                            </span>
                          </a>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <a href="/addevent" onClick={handleSidebarNavigation('/addevent')}>
                            <span className="flex">
                              <CalendarPlus className="mr-2 h-4 w-4" />
                              Add New
                            </span>
                          </a>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </Collapsible>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessCourseManagement') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/course-management" onClick={handleSidebarNavigation('/course-management')}>
                      <BookOpen className="mr-2 h-4 w-4" />
                      Course Management
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessCollegeFees') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/college-fees" onClick={handleSidebarNavigation('/college-fees')}>
                      <GraduationCap className="mr-2 h-4 w-4" />
                      College Fees
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessPCSAT') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/pcsat-registrations" onClick={handleSidebarNavigation('/pcsat-registrations')}>
                      <ClipboardList className="mr-2 h-4 w-4" />
                      PCSAT Registrations
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessOptionForm') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/option-form-registrations" onClick={handleSidebarNavigation('/option-form-registrations')}>
                      <ListOrdered className="mr-2 h-4 w-4" />
                      Option Form
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessGrandMockTest') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/grand-mock-tests" onClick={handleSidebarNavigation('/grand-mock-tests')}>
                      <GraduationCap className="mr-2 h-4 w-4" />
                      Grand Mock Tests
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasAccess(userRole, 'canAccessTestSeries') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a href="/test-series" onClick={handleSidebarNavigation('/test-series')}>
                      <FileText className="mr-2 h-4 w-4" />
                      Test Series
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarSeparator />
      <SidebarFooter>
        <SidebarMenu>
          <ModeToggle />
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex w-full items-center gap-2 p-2 rounded-md hover:bg-muted">
                  <User className="h-6 w-6" />
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium">{userRole === 'superAdmin' ? 'Super Admin' : userRole === 'blogger' ? 'Blogger' : userRole === 'broadcastOfficer' ? 'Broadcast Officer' : userRole === 'payoutOfficer' ? 'Payout Officer' : userRole === 'COURSE' ? 'Course Manager' : userRole === 'collegeAdmin' ? 'College Admin' : userRole === 'caller' ? 'Caller' : userRole === 'marketing' ? 'Marketing' : 'T3 Onboarding'}</span>
                    <span className="text-xs text-muted-foreground">{user?.userId}</span>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>
                  Logged in as
                  <br />
                  <span className="text-xs text-muted-foreground">{user?.userId}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
